// Text input component for playing games

import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useValidatedInput } from '@/hooks';
import { cn } from '@/utilities/cn';
import { getCharCounterClass } from '@/utilities/inputFeedback';

export type PlayingTextInputProps = {
  rules?: string[];
  transforms?: string[];
  initialValue?: string;
  label?: string;
  placeholder?: string;
  maxLength?: number;
  autoFocus?: boolean;
  autoCapitalize?: 'off' | 'sentences' | 'words' | 'characters';
  showCharCounter?: boolean;
  submitLabel?: string;
  /** Blocks submission while the caller waits on something external */
  disabled?: boolean;
  onSubmit: (value: string) => void | Promise<void>;
};

const PlayingTextInput: React.FC<PlayingTextInputProps> = ({
  rules,
  transforms,
  initialValue,
  label,
  placeholder,
  maxLength,
  autoFocus = true,
  autoCapitalize = 'off',
  showCharCounter = false,
  submitLabel = 'Submit',
  disabled = false,
  onSubmit,
}) => {
  const inputId = React.useId();
  const input = useValidatedInput({
    rules,
    transforms,
    initialValue,
    checkModCensors: true,
    onSubmit,
  });

  return (
    <form
      className="w-full"
      onSubmit={(event) => {
        event.preventDefault();
        void input.submit();
      }}
    >
      <FieldGroup>
        <Field data-invalid={!!input.error}>
          {label ? (
            <FieldLabel htmlFor={inputId} className="sr-only">
              {label}
            </FieldLabel>
          ) : null}
          {showCharCounter && typeof maxLength === 'number' ? (
            <div className="flex w-full items-center justify-between gap-3">
              <span aria-hidden className="min-w-0 shrink-0" />
              <div className="text-xs leading-none">
                <span
                  className={cn(
                    getCharCounterClass(input.value.length, maxLength)
                  )}
                >
                  {input.value.length}/{maxLength}
                </span>
              </div>
            </div>
          ) : null}
          <Input
            {...input.inputProps}
            id={inputId}
            placeholder={placeholder}
            autoFocus={autoFocus}
            autoCapitalize={autoCapitalize}
            maxLength={maxLength}
            aria-invalid={!!input.error}
            className="h-11 text-center text-base md:text-base"
          />
          {input.error ? <FieldError>{input.error}</FieldError> : null}
        </Field>
        <Button
          type="submit"
          size="xl"
          className="w-full cursor-pointer"
          disabled={disabled || input.isSubmitting}
        >
          {input.isSubmitting ? <Spinner /> : submitLabel}
        </Button>
      </FieldGroup>
    </form>
  );
};

export default PlayingTextInput;
